import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useBranding, BrandLogo, BrandFooter } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, ShieldCheck, Users, FileCheck2, Building2, Receipt, Percent, Landmark, ScrollText, LifeBuoy,
  SlidersHorizontal, UserCog, LogOut, Menu, X, Home, FilePlus2, Settings, Upload, BarChart3, Wallet, MessagesSquare, Link2,
  Zap, AlertTriangle, Ticket, Megaphone, UserX,
} from "lucide-react";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, hideFor: ["system_admin", "help_desk"] },
  { to: "/app/support", label: "Support Inbox", icon: MessagesSquare, perm: ["support.inbox", "support.team"] },
  { to: "/app/link-requests", label: "Account-Linking Requests", icon: Link2, perm: "links.review" },
  { to: "/app/tickets", label: "Support Tickets", icon: Ticket, perm: "tickets.manage" },
  { to: "/app/advisories", label: "Outages & Advisories", icon: Zap, perm: "outages.manage" },
  { to: "/app/reports", label: "Interruption Reports", icon: AlertTriangle, perm: "reports.manage" },
  { to: "/app/announcements", label: "Announcements", icon: Megaphone, perm: "announcements.manage" },
  { to: "/app/deletion-requests", label: "Deletion Requests", icon: UserX, perm: "deletion.review" },
  { to: "/app/my-accounts", label: "My Accounts", icon: Home, role: "consumer" },
  { to: "/app/my-applications", label: "My Applications", icon: FilePlus2, role: "consumer" },
  { to: "/app/dashboards/accounting", label: "Accounting Dashboard", icon: BarChart3, perm: "accounting.dashboard" },
  { to: "/app/dashboards/treasury", label: "Treasury Dashboard", icon: Wallet, perm: "treasury.dashboard" },
  { to: "/app/applications", label: "Registration Review", icon: FileCheck2, perm: "registration.review" },
  { to: "/app/accounts", label: "Subscriber Accounts", icon: Building2, perm: ["accounts.view", "twa.view", "accounts.manage"] },
  { to: "/app/twa", label: "TWA Review", icon: Landmark, perm: "twa.view" },
  { to: "/app/payments", label: "Payments", icon: Receipt, perm: ["payments.record", "payments.wht_review"] },
  { to: "/app/tax-rules", label: "Tax Rules", icon: Percent, perm: "twa.view" },
  { to: "/app/billing-upload", label: "Monthly Billing Upload", icon: Upload, perm: "billing.upload" },
  { to: "/app/admin/users", label: "Users", icon: Users, perm: "users.view" },
  { to: "/app/admin/security-policy", label: "Security Policy", icon: ShieldCheck, perm: "policy.manage" },
  { to: "/app/admin/recovery", label: "Account Recovery", icon: LifeBuoy, perm: "recovery.manage" },
  { to: "/app/admin/registration-config", label: "Registration Setup", icon: SlidersHorizontal, perm: "registration.configure" },
  { to: "/app/admin/audit", label: "Audit Log", icon: ScrollText, perm: "audit.view" },
  { to: "/app/profile", label: "Profile & Security", icon: UserCog },
  { to: "/app/admin/admin-settings", label: "Admin Settings", icon: SlidersHorizontal, perm: "admin_settings.manage" },
  { to: "/app/admin/system-settings", label: "System Settings", icon: Settings, perm: "settings.manage" },
];

function NavList({ onNavigate }) {
  const { user, can } = useAuth();
  const items = NAV.filter((n) => {
    if (n.hideFor?.includes(user.role)) return false;
    if (n.role) return user.role === n.role;
    return !n.perm || [].concat(n.perm).some(can);
  });
  return (
    <nav className="space-y-1">
      {items.map(({ to, label, icon: Icon, end }) => (
        <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className="flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors nav-link">
          <Icon className="h-4 w-4 shrink-0" /> <span className="truncate">{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function Brand() {
  const { brand } = useBranding();
  return (
    <div className="flex items-center gap-2 px-2 mb-8 min-w-0">
      <BrandLogo kind="logo_dark" />
      <div className="min-w-0">
        <p className="font-display font-bold leading-tight truncate" style={{ color: "var(--c-header-text)" }} data-testid="brand-short-name">{brand?.identity.short_name}</p>
        <p className="text-[11px] uppercase tracking-widest opacity-60 truncate">Management Portal</p>
      </div>
    </div>
  );
}

function ChangePasswordGate() {
  const { reloadUser, logout } = useAuth();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) return toast.error("Passwords do not match");
    try { await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password }); toast.success("Password changed"); await reloadUser(); }
    catch (ex) { toast.error(errMsg(ex)); }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4 bg-white border rounded-lg p-6" data-testid="change-password-gate">
        <BrandLogo /><h1 className="text-xl font-bold">Change your temporary password</h1>
        <p className="text-sm text-slate-500">You signed in with a temporary password. Set a new one to continue.</p>
        {[["current_password", "Temporary password"], ["new_password", "New password"], ["confirm", "Confirm new password"]].map(([k, l]) => (
          <div key={k} className="space-y-1.5"><Label>{l}</Label><Input type="password" required value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`cp-${k}`} /></div>
        ))}
        <Button type="submit" className="w-full" data-testid="cp-submit">Change password</Button>
        <Button type="button" variant="link" className="px-0" onClick={logout}>Sign out</Button>
      </form>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { brand } = useBranding();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  if (user === null) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ChangePasswordGate />;
  const footer = (
    <div className="border-t border-white/10 pt-4 mt-6">
      <p className="text-sm truncate" style={{ color: "var(--c-header-text)" }} data-testid="current-user-email">{user.email}</p>
      <p className="text-xs mb-3" style={{ color: "var(--c-sidebar-active-text)" }} data-testid="current-user-role">{user.role_label}</p>
      <Button variant="ghost" size="sm" className="w-full justify-start nav-link" onClick={async () => { await logout(); nav("/login"); }} data-testid="logout-button">
        <LogOut className="h-4 w-4 mr-2" /> Sign out
      </Button>
    </div>
  );
  const sidebar = (extra) => (
    <aside className={`app-sidebar p-4 flex flex-col justify-between overflow-y-auto ${extra}`}>
      <div><Brand /><NavList onNavigate={() => setOpen(false)} /></div>{footer}
    </aside>
  );
  return (
    <div className="min-h-screen flex">
      <div className="hidden md:block w-64 shrink-0 sticky top-0 h-screen">{sidebar("h-full")}</div>
      {open && (
        <div className="fixed inset-0 z-50 md:hidden bg-slate-950/60" onClick={() => setOpen(false)}>
          <div className="w-72 h-full relative" onClick={(e) => e.stopPropagation()}>
            <X className="h-5 w-5 absolute right-3 top-3 z-10" style={{ color: "var(--c-sidebar-text)" }} onClick={() => setOpen(false)} />
            {sidebar("h-full")}
          </div>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="md:hidden sticky top-0 z-40 px-4 py-3 flex items-center gap-3 app-header">
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></button>
          <BrandLogo className="h-7 w-7" /><span className="font-display font-bold truncate">{brand?.identity.short_name}</span>
        </header>
        <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10"><Outlet /></main>
        <BrandFooter />
      </div>
    </div>
  );
}

export function RequirePerm({ perm, role, children }) {
  const { user, can } = useAuth();
  if (role && user.role !== role) return <Navigate to="/app" replace />;
  if (perm && ![].concat(perm).some(can)) return <Navigate to={user.role === "system_admin" ? "/app/admin/users" : user.role === "help_desk" ? "/app/support" : "/app"} replace />;
  return children;
}
